/**
 * AirlineBound Share-to-Save Component
 * 
 * Integration Instructions:
 * 1. Install dependencies: yarn add react-icons lucide-react
 * 2. Copy this file to your components folder
 * 3. Add the CSS styles from ShareToSave.css to your stylesheets
 * 4. Import and use <ShareToSaveCard /> in your pricing page
 * 
 * Usage:
 * import { ShareToSaveCard } from './ShareToSaveCard';
 * <ShareToSaveCard onUnlock={(code) => console.log('Discount code:', code)} />
 */

import React, { useState, useCallback, useRef } from "react";
import { Check, X, Copy, ExternalLink, Download, Share2, Loader2 } from "lucide-react";
import { FaInstagram } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { MapPreview } from "./MapPreview";

const API = process.env.REACT_APP_BACKEND_URL;

// Generate unique discount code
const generateDiscountCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const prefix = 'AIRBOUND';
  let code = prefix;
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

// Confetti Component
const Confetti = ({ active }) => {
  if (!active) return null;
  
  const colors = ['#f87171', '#fb7185', '#f43f5e', '#fb923c', '#fbbf24'];
  const confettiPieces = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 2,
    color: colors[Math.floor(Math.random() * colors.length)],
    size: Math.random() * 8 + 4
  }));

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {confettiPieces.map(piece => (
        <div
          key={piece.id}
          className="confetti absolute"
          style={{
            left: `${piece.left}%`,
            animationDelay: `${piece.delay}s`,
            backgroundColor: piece.color,
            width: piece.size,
            height: piece.size,
            borderRadius: Math.random() > 0.5 ? '50%' : '0'
          }}
        />
      ))}
    </div>
  );
};

// Simple Share Modal - Focus on the hook (the tracker)
const ShareModal = ({ onClose, onShare, countdown }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = 'https://airlinebound.com/tracker';
  const shareText = '✈️ Track flights in real-time on this amazing Azimuthal map! Check it out:';
  
  const handleNativeShare = async () => {
    // Use Web Share API if available (works great on mobile!)
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'AirlineBound - Flight Tracker',
          text: shareText,
          url: shareUrl,
        });
        onShare && onShare();
      } catch (err) {
        // User cancelled or error - that's ok
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy link
      handleCopyLink();
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = `${shareText} ${shareUrl}`;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOpenInstagram = () => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      window.location.href = 'instagram://';
      setTimeout(() => {
        window.open('https://www.instagram.com/', '_blank');
      }, 500);
    } else {
      window.open('https://www.instagram.com/', '_blank');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="relative max-w-md w-full flex flex-col items-center" onClick={e => e.stopPropagation()}>
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute -top-2 -right-2 z-10 w-8 h-8 bg-slate-800 hover:bg-slate-700 rounded-full flex items-center justify-center text-white"
        >
          <X size={18} />
        </button>

        {/* Main Card */}
        <div className="bg-slate-900 rounded-2xl overflow-hidden w-full">
          {/* Countdown Header - Urgency messaging */}
          {countdown > 0 && (
            <div className="bg-gradient-to-r from-red-500 to-red-400 px-4 py-3 flex items-center justify-center gap-3">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span className="text-white font-bold">Discount code expires in {countdown}s</span>
            </div>
          )}
          
          {/* Value Proposition - Under countdown */}
          <div className="px-4 py-4 text-center">
            <p className="text-slate-200 text-sm leading-relaxed">
              The only online flight tracking platform that displays real-time flight status on an <span className="text-red-400 font-semibold">Azimuthal Equidistant Map</span>
            </p>
          </div>
          
          {/* Hero - Live Map Preview */}
          <div className="relative">
            <MapPreview 
              className="w-full" 
              style={{ height: '180px', borderRadius: '0' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent pointer-events-none" />
          </div>
          
          {/* Centered Website Link */}
          <div className="text-center py-3">
            <p className="text-red-400 text-sm font-medium">airlinebound.com/tracker</p>
          </div>

          {/* Share Section */}
          <div className="px-6 pb-6 space-y-4">
            <p className="text-slate-300 text-center">
              Share with your friends and unlock <span className="text-red-400 font-bold">90% off</span> Premium!
            </p>

            {/* Share Buttons */}
            <div className="space-y-3">
              {/* Primary: Native Share (mobile) or Copy Link (desktop) */}
              <button 
                onClick={handleNativeShare}
                className="w-full py-4 px-6 rounded-lg font-bold text-white flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-95"
                style={{ 
                  background: 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)'
                }}
              >
                <Share2 size={20} />
                {navigator.share ? 'Share Now' : (copied ? 'Copied!' : 'Copy Link to Share')}
              </button>

              {/* Secondary: Open Instagram */}
              <button 
                onClick={handleOpenInstagram}
                className="w-full py-3 px-6 rounded-lg font-semibold text-white flex items-center justify-center gap-2 transition-all"
                style={{ 
                  background: 'linear-gradient(135deg, #833AB4 0%, #E1306C 50%, #F77737 100%)'
                }}
              >
                <FaInstagram size={18} />
                Open Instagram to Share
              </button>

              {/* Copy link as text option */}
              <div className="flex items-center gap-2 bg-slate-800 rounded-lg px-4 py-3">
                <input 
                  type="text" 
                  value={shareUrl}
                  readOnly
                  className="flex-1 bg-transparent text-slate-300 text-sm outline-none"
                />
                <button 
                  onClick={handleCopyLink}
                  className="text-red-400 hover:text-red-300 transition-colors"
                >
                  {copied ? <Check size={18} /> : <Copy size={18} />}
                </button>
              </div>
            </div>

            {/* Skip option */}
            <button 
              onClick={onClose}
              className="w-full text-slate-500 hover:text-slate-300 text-sm underline"
            >
              I've shared, close this
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Feature List Item
const FeatureItem = ({ text, included = true }) => (
  <div className="flex items-center gap-3 py-1">
    <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${included ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700/50 text-slate-500'}`}>
      {included ? <Check size={12} /> : <X size={12} />}
    </div>
    <span className={included ? 'text-slate-300' : 'text-slate-500 line-through'}>{text}</span>
  </div>
);

// Main Share-to-Save Card Component
export const ShareToSaveCard = ({ onUnlock }) => {
  // Check localStorage for persisted state on mount
  const getInitialState = () => {
    const saved = localStorage.getItem('shareToSave');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Check if code is still valid (within 24 hours)
      if (parsed.timestamp && Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
        return parsed;
      }
    }
    return { state: 'idle', discountCode: '', countdown: 0 };
  };

  const initialState = getInitialState();
  const [state, setState] = useState(initialState.state);
  const [discountCode, setDiscountCode] = useState(initialState.discountCode);
  const [copied, setCopied] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showStoryTemplate, setShowStoryTemplate] = useState(false);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [countdown, setCountdown] = useState(initialState.countdown);
  
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();

  // Persist state to localStorage
  const saveState = useCallback((newState, code, count) => {
    localStorage.setItem('shareToSave', JSON.stringify({
      state: newState,
      discountCode: code,
      countdown: count,
      timestamp: Date.now()
    }));
  }, []);

  const handleShareClick = () => {
    // Show the share modal
    setShowStoryTemplate(true);
    setState('sharing');
    setCountdown(15); // 15 seconds to share
    saveState('sharing', '', 15);
  };

  // Countdown timer effect
  React.useEffect(() => {
    if (countdown > 0 && state === 'sharing') {
      const timer = setTimeout(() => {
        const newCount = countdown - 1;
        setCountdown(newCount);
        saveState('sharing', '', newCount);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && state === 'sharing') {
      // Countdown finished - unlock the code
      const code = generateDiscountCode();
      setDiscountCode(code);
      setState('unlocked');
      setShowConfetti(true);
      saveState('unlocked', code, 0);
      setTimeout(() => setShowConfetti(false), 3000);
      if (onUnlock) onUnlock(code);
    }
  }, [countdown, state, onUnlock, saveState]);

  const handleClaimDiscount = () => {
    // Skip countdown if they claim early
    setCountdown(0);
    const code = generateDiscountCode();
    setDiscountCode(code);
    setState('unlocked');
    setShowConfetti(true);
    saveState('unlocked', code, 0);
    setTimeout(() => setShowConfetti(false), 3000);
    if (onUnlock) onUnlock(code);
  };

  const copyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(discountCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = discountCode;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        window.prompt('Copy this discount code:', discountCode);
      }
      document.body.removeChild(textArea);
    }
  }, [discountCode]);

  const handleRedeem = async () => {
    // If not logged in, redirect to register
    if (!isAuthenticated) {
      navigate('/register');
      return;
    }
    
    setIsRedeeming(true);
    
    try {
      const response = await axios.post(
        `${API}/payments/social-checkout`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Unable to start checkout. Please try again or contact support.');
      setIsRedeeming(false);
    }
  };

  return (
    <>
      <Confetti active={showConfetti} />
      {showStoryTemplate && (
        <ShareModal 
          onClose={() => setShowStoryTemplate(false)} 
          onShare={() => {}} // Optional callback when shared
          countdown={countdown}
        />
      )}
      
      <div 
        className="relative backdrop-blur-md rounded-xl p-6 md:p-8 overflow-hidden"
        style={{ 
          background: 'rgba(26, 39, 68, 0.95)',
          border: '2px solid rgba(248, 113, 113, 0.5)',
          boxShadow: '0 0 40px rgba(248, 113, 113, 0.12), 0 25px 50px -12px rgba(0, 0, 0, 0.5)'
        }}
      >
        {/* Share & Save Badge */}
        <div className="absolute -top-1 left-1/2 -translate-x-1/2">
          <div 
            className="bg-gradient-to-r from-red-400 to-red-500 text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wider flex items-center gap-1"
            style={{ boxShadow: '0 0 10px rgba(248, 113, 113, 0.5)' }}
          >
            <FaInstagram size={12} />
            Share & Save 90%
          </div>
        </div>

        {/* Card Content */}
        <div className="mt-6">
          {/* Tier Header */}
          <div className="text-center mb-6">
            <h3 className="text-xl font-semibold text-white mb-1">Social Unlock</h3>
            <p className="text-slate-400 text-sm mb-3">Share to unlock exclusive pricing</p>
            <div className="flex items-center justify-center gap-2">
              <span className="text-slate-500 line-through text-lg">$4.99</span>
              <span className="text-5xl md:text-6xl font-bold text-white">$0.49</span>
            </div>
            <p className="text-red-400 text-sm mt-1 font-medium">28-day Premium Access</p>
          </div>

          {/* Value Proposition */}
          <div className="bg-red-400/10 border border-red-400/20 rounded-lg p-4 mb-6">
            <p className="text-slate-300 text-sm text-center">
              Share AirlineBound with your network and unlock <span className="text-red-400 font-semibold">90% off</span> Premium features!
            </p>
          </div>

          {/* Features */}
          <div className="space-y-1 mb-6">
            <FeatureItem text="Everything in Free" included />
            <FeatureItem text="Track ALL countries worldwide" included />
            <FeatureItem text="Flight path trails" included />
            <FeatureItem text="Follow flights in real-time" included />
            <FeatureItem text="No advertisements" included />
          </div>

          {/* Action Area - Idle State */}
          {state === 'idle' && (
            <div className="space-y-3">
              <button 
                onClick={handleShareClick}
                className="w-full py-4 px-6 rounded-lg font-bold text-white flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-95"
                style={{ 
                  background: 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)',
                  animation: 'pulse-coral 2s infinite'
                }}
                data-testid="share-instagram-btn"
              >
                <FaInstagram size={20} />
                Share & Unlock 90% Off
              </button>
              
              <p className="text-slate-500 text-xs text-center">
                Share <span className="text-red-400">airlinebound.com</span> to your Instagram Story and unlock your discount!
              </p>
            </div>
          )}

          {/* Sharing State - With Countdown */}
          {state === 'sharing' && (
            <div className="space-y-4">
              {/* Countdown Circle */}
              <div className="flex flex-col items-center gap-3">
                <div className="relative w-20 h-20">
                  {/* Background circle */}
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="40"
                      cy="40"
                      r="36"
                      fill="none"
                      stroke="rgba(248, 113, 113, 0.2)"
                      strokeWidth="6"
                    />
                    {/* Progress circle */}
                    <circle
                      cx="40"
                      cy="40"
                      r="36"
                      fill="none"
                      stroke="#f87171"
                      strokeWidth="6"
                      strokeLinecap="round"
                      strokeDasharray={`${(countdown / 15) * 226} 226`}
                      className="transition-all duration-1000"
                    />
                  </svg>
                  {/* Countdown number */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-3xl font-bold text-white">{countdown}</span>
                  </div>
                </div>
                <p className="text-slate-400 text-sm text-center">
                  {countdown > 0 ? (
                    <>Sharing <span className="text-red-400">airlinebound.com/tracker</span>...</>
                  ) : (
                    <span className="text-emerald-400">Unlocking your code...</span>
                  )}
                </p>
                
                {/* Open share modal again */}
                <button 
                  onClick={() => setShowStoryTemplate(true)}
                  className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  <Share2 size={16} />
                  Open Share Options
                </button>
              </div>
              
              {/* Skip button */}
              <button 
                onClick={handleClaimDiscount}
                className="w-full py-2 px-4 rounded-lg text-sm text-slate-500 hover:text-slate-300 hover:bg-slate-700/30 transition-all"
                data-testid="claim-discount-btn"
              >
                Already shared? Click here
              </button>
            </div>
          )}

          {/* Unlocked State */}
          {state === 'unlocked' && (
            <div className="space-y-4">
              {/* Success Message */}
              <div className="flex items-center justify-center gap-2 text-emerald-400">
                <div className="w-6 h-6 bg-emerald-500/20 rounded-full flex items-center justify-center">
                  <Check size={14} />
                </div>
                <span className="font-semibold">Discount Unlocked!</span>
              </div>

              {/* Discount Code Display */}
              <div 
                className="rounded-lg p-4"
                style={{ 
                  background: 'linear-gradient(135deg, rgba(248, 113, 113, 0.1) 0%, rgba(15, 23, 42, 0.9) 100%)',
                  border: '1px solid rgba(248, 113, 113, 0.3)'
                }}
              >
                <p className="text-slate-400 text-xs uppercase tracking-wider mb-2 text-center">Your Discount Code</p>
                <div className="flex items-center justify-between bg-black/30 rounded-lg px-4 py-3">
                  <code className="font-mono text-xl text-red-400 tracking-wider" data-testid="discount-code">
                    {discountCode}
                  </code>
                  <button 
                    onClick={copyToClipboard}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    data-testid="copy-code-btn"
                  >
                    {copied ? (
                      <Check size={18} className="text-emerald-400" />
                    ) : (
                      <Copy size={18} className="text-slate-400" />
                    )}
                  </button>
                </div>
                <p className="text-slate-500 text-xs text-center mt-2">
                  Valid for 28 days from activation
                </p>
              </div>

              {/* CTA */}
              <button 
                onClick={handleRedeem}
                disabled={isRedeeming}
                className="w-full py-3 px-6 rounded-lg font-bold bg-red-400 hover:bg-red-500 text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="redeem-now-btn"
              >
                {isRedeeming ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Processing...
                  </>
                ) : (
                  isAuthenticated ? 'Redeem Now - $0.49' : 'Sign Up to Redeem'
                )}
              </button>
            </div>
          )}
        </div>

        {/* Decorative Corner Elements */}
        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-red-400/10 to-transparent rounded-bl-full" />
        <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-red-400/5 to-transparent rounded-tr-full" />
      </div>
    </>
  );
};

export default ShareToSaveCard;
